/**
 * A hiánytétel felirata — `state` és `certainty` PÁRJÁBÓL.
 *
 * Miért függvény, és miért nem JSON-kulcs: a felirat két mező függvénye, nem
 * egy kulcs értéke. Öt állapot és két bizonyosság hét feliratot ad, nem tízet
 * — és nincs hatodik enum-érték. A `Beazonosítatlan` nem állapot, hanem
 * `OPEN` + `SUSPECTED`; a `Megkérdezve` sem az, hanem `REQUESTED` + `SUSPECTED`.
 * (`docs/szojegyzek.md` — „A felületi felirat nem enum-érték".)
 *
 * A `t()` hívások SZÁNDÉKOSAN itt, literál kulccsal állnak, nem a hívó
 * oldalán egy visszaadott kulcson át: így az i18n-kapu statikusan látja
 * mind a hetet. Egy `t(statusLabelKey(x))` alakot a mérő nem venne észre.
 *
 * Ez a fájl a `packages/i18n` kanonikus forrásában él, és onnan másolódik
 * a szervizekbe (`python3 scripts/shared-sync.py`). Itt szerkeszd, ne a másolatot.
 */

export type DiscrepancyState =
  | 'OPEN'
  | 'WAITING'
  | 'REQUESTED'
  | 'RESOLVED'
  | 'DISMISSED'
  /** A NAV-tételt visszavonták: sztornó vagy módosító okirat írta felül. */
  | 'SUPERSEDED';

export type DiscrepancyCertainty = 'PROVEN' | 'SUSPECTED';

/**
 * A KILÉPETT állapotok. Típusként is ki van mondva, hogy az `isClosed` TÍPUS-
 * ŐR lehessen: így a szűkítés után a fordító tudja, mi maradt — a hívónak nem
 * kell kézzel felsorolnia a nyitott állapotokat, és a negyedik kilépés
 * bevezetésekor a hívóoldal NEM FORDUL LE, ahelyett hogy csendben átengedné.
 */
export type ClosedDiscrepancyState = 'RESOLVED' | 'DISMISSED' | 'SUPERSEDED';

/**
 * A gyanítottnak nincs `WAITING`-je: a türelmi idő órája a NAV bejelentésétől
 * ketyeg (`firstSeenAt`), a banksor mögött pedig nincs bejelentés. Ezt a
 * korlátot a típus tartja, nem egy futásidejű ellenőrzés — így a fordító szól,
 * ha valaki mégis ilyen párost próbál előállítani.
 */
export type DiscrepancyStatus =
  | { state: 'WAITING'; certainty: 'PROVEN' }
  | {
      state: 'OPEN' | 'REQUESTED' | 'RESOLVED' | 'DISMISSED' | 'SUPERSEDED';
      certainty: DiscrepancyCertainty;
    };

/**
 * Minimális fordítófüggvény-típus. Szándékosan nem az i18next `TFunction`-je:
 * ezt a fájlt a NestJS backend is használja az értesítés-sablonokhoz, és nem
 * akarjuk odahúzni a React-oldali i18n-függőséget.
 */
export type Translate = (key: string) => string;

export function statusLabel(t: Translate, status: DiscrepancyStatus): string {
  switch (status.state) {
    case 'WAITING':
      return t('discrepancy.status.waiting');

    case 'OPEN':
      return status.certainty === 'PROVEN'
        ? t('discrepancy.status.chaseable')
        : t('discrepancy.status.unidentified');

    case 'REQUESTED':
      return status.certainty === 'PROVEN'
        ? t('discrepancy.status.requested')
        : t('discrepancy.status.asked');

    case 'RESOLVED':
      return t('discrepancy.status.resolved');

    case 'DISMISSED':
      return t('discrepancy.status.dismissed');

    case 'SUPERSEDED':
      return t('discrepancy.status.superseded');

    default: {
      // Kimerítőség-ellenőrzés: ha a `state` enum bővül, ez a sor nem fordul le.
      const exhaustive: never = status;

      // FUTÁSIDŐBEN NEM DOBUNK — ugyanaz az érv, mint a `docTypeLabel.ts`-ben:
      // az érték a hálózatról jön, és egy dobott kivétel a hiánylista MINDEN
      // sorát elvinné render közben.
      //
      // ITT VISZONT NINCS GYŰJTŐÉRTÉK: a `DocType`-nak van `OTHER`-je, a
      // hiány-állapotnak nincs. Egy meglévő felirat visszaadása JELENTÉST
      // találna ki — a „Beazonosítatlan" nem „ismeretlen", hanem
      // `OPEN` + `SUSPECTED`. Ezért a nyers érték megy ki: csúnya, de igaz,
      // és a könyvelő látja, hogy valami újat kapott a szervertől.
      void exhaustive;
      return (status as DiscrepancyStatus).state;
    }
  }
}

/**
 * Kilépett-e a tétel a hiánylistáról. HÁROM kilépés van — a sor ELTŰNIK, nem
 * elszürkül (`docs/szojegyzek.md`):
 *
 *   • `RESOLVED` — megjött a bizonylat;
 *   • `DISMISSED` — a könyvelő elutasította;
 *   • `SUPERSEDED` — a NAV-tételt visszavonták, tehát nincs mit bekérni.
 *
 * ⚠️ EZ A FÜGGVÉNY AZ EGYETLEN HELY, ahol a három együtt van kimondva. Aki
 * `state === 'RESOLVED' || state === 'DISMISSED'`-et ír helyette, a következő
 * kilépésnél NÉMÁN marad el — pontosan úgy, ahogy a `SUPERSEDED` bevezetése
 * mutatta meg: a `liveItemsOf` szűrője enélkül egy visszavont számlát is
 * kiküldött volna a bekérő levélben.
 */
export function isClosed(state: DiscrepancyState): state is ClosedDiscrepancyState {
  return state === 'RESOLVED' || state === 'DISMISSED' || state === 'SUPERSEDED';
}

/**
 * A KILÉPETT ÁLLAPOTOK, FUTÁSIDŐBEN IS — mert nem minden hely tud típusőrt
 * használni.
 *
 * ⚠️ A PRISMA `where`-EK EZ ELŐL REJTVE MARADNAK. Egy
 * `state: { notIn: ['RESOLVED', 'DISMISSED'] }` érvényes TypeScript marad
 * akkor is, amikor a kilépések száma háromra nő — a fordító nem szól, a
 * lekérdezés viszont attól kezdve HAMISAT mond. A `SUPERSEDED` bevezetése
 * négy ilyen helyet talált. Innentől mindegyik ezt a listát használja.
 *
 * A teljességét a `Hianyzo` őrzi, nem a fegyelem.
 */
export const CLOSED_DISCREPANCY_STATES = [
  'RESOLVED',
  'DISMISSED',
  'SUPERSEDED',
] as const satisfies readonly ClosedDiscrepancyState[];

type HianyzoKilepes = Exclude<ClosedDiscrepancyState, (typeof CLOSED_DISCREPANCY_STATES)[number]>;
const _mindenKilepesFelsorolva: HianyzoKilepes extends never ? true : never = true;
void _mindenKilepesFelsorolva;
